export type PlaybackLifecycleStatus =
  | "idle"
  | "preparing"
  | "ready"
  | "playing"
  | "buffering"
  | "recovering"
  | "ended"
  | "failed";

export type PlaybackLifecycleState = {
  status: PlaybackLifecycleStatus;
  decoderReady: boolean;
  error: string;
  generation: number;
  paused: boolean;
  muted: boolean;
  volume: number;
  position: number;
  duration: number;
  buffered: number;
};

export type PlaybackLifecycleEvent =
  | { type: "LOAD"; recovering?: boolean }
  | { type: "READY" }
  | { type: "PLAYING" }
  | { type: "BUFFERING"; active: boolean }
  | { type: "ENDED" }
  | { type: "FAILED"; message: string }
  | { type: "SET_PAUSED"; value: boolean }
  | { type: "SET_MUTED"; value: boolean }
  | { type: "SET_VOLUME"; value: number }
  | { type: "SET_POSITION"; value: number }
  | { type: "SET_DURATION"; value: number }
  | { type: "SET_BUFFERED"; value: number }
  | { type: "RESET_MEDIA_CLOCK" };

export const initialPlaybackLifecycle: PlaybackLifecycleState = {
  status: "idle",
  decoderReady: false,
  error: "",
  generation: 0,
  paused: false,
  muted: false,
  volume: 1,
  position: 0,
  duration: 0,
  buffered: 0,
};

const finiteNonNegative = (value: number, fallback: number) =>
  Number.isFinite(value) ? Math.max(0, value) : fallback;

export function playbackLifecycleReducer(
  state: PlaybackLifecycleState,
  event: PlaybackLifecycleEvent,
): PlaybackLifecycleState {
  switch (event.type) {
    case "LOAD":
      return {
        ...state,
        status: event.recovering ? "recovering" : "preparing",
        decoderReady: false,
        error: "",
        generation: state.generation + 1,
      };
    case "READY":
      return { ...state, status: "ready", decoderReady: true, error: "" };
    case "PLAYING":
      return { ...state, status: "playing", decoderReady: true, error: "", paused: false };
    case "BUFFERING":
      if (state.status === "failed" || state.status === "ended") return state;
      // Native engines commonly announce an initial buffer before onLoad has
      // established duration/tracks. That is still decoder preparation, not a
      // playable decoder entering a later buffering phase. Preserve the
      // preparing/recovering state so startup deadlines and UI remain honest.
      if (event.active && !state.decoderReady) return state;
      if (event.active) return { ...state, status: "buffering" };
      return state.status === "buffering"
        ? { ...state, status: state.paused ? "ready" : "playing" }
        : state;
    case "ENDED":
      return { ...state, status: "ended", error: "", position: state.duration || state.position };
    case "FAILED":
      return { ...state, status: "failed", decoderReady: false, error: event.message };
    case "SET_PAUSED":
      return {
        ...state,
        paused: event.value,
        status: event.value && state.status === "playing" ? "ready" : state.status,
      };
    case "SET_MUTED":
      return { ...state, muted: event.value };
    case "SET_VOLUME": {
      const volume = Number.isFinite(event.value) ? Math.max(0, Math.min(1, event.value)) : state.volume;
      return { ...state, volume };
    }
    case "SET_POSITION":
      return { ...state, position: finiteNonNegative(event.value, state.position) };
    case "SET_DURATION":
      return { ...state, duration: finiteNonNegative(event.value, state.duration) };
    case "SET_BUFFERED":
      return { ...state, buffered: finiteNonNegative(event.value, state.buffered) };
    case "RESET_MEDIA_CLOCK":
      return { ...state, duration: 0, buffered: 0 };
    default:
      return state;
  }
}
